import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/firebase";
import CustomDropdown from "../components/CustomDropdown";
import {
    eachDayOfInterval,
    isSunday,
    parseISO,
    isAfter,
    isBefore,
    startOfMonth,
    endOfMonth,
    startOfYear,
    endOfYear,
    format,
    getDate,
    getDaysInMonth,
    startOfWeek,
} from "date-fns";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../utils/themeUtils";
import { getTimesheetPermissions } from "../utils/permissions";
import { Check, X, ArrowLeft, Download } from "lucide-react";

export interface User {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    joiningDate?: string;
}

interface TimeSheetTableProps {
    organizationId: string;
    users: User[];
    onBack: () => void;
    activeTab?: 'CheckIn' | 'TimeSheet';
    onTabChange?: (tab: 'CheckIn' | 'TimeSheet') => void;
    currentUser?: any;
    showBackButton?: boolean;
}

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

const TimeSheetTable: React.FC<TimeSheetTableProps> = ({
    organizationId,
    users,
    onBack,
    activeTab,
    onTabChange,
    currentUser,
    showBackButton,
}) => {
    const now = new Date();
    const { colorMode } = useColorMode();
    const isDark = colorMode === "dark";
    const theme = getThemeClasses(isDark);

    const [selectedUserId, setSelectedUserId] = useState<string>("all");

    const sortedUsers = React.useMemo(() => {
        return [...users].sort((a, b) => {
            const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
            const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
            return nameA.localeCompare(nameB);
        });
    }, [users]);

    const filteredUsers = React.useMemo(() => {
        if (selectedUserId === "all") return sortedUsers;
        return sortedUsers.filter(u => u.id === selectedUserId);
    }, [sortedUsers, selectedUserId]);

    const tsPerms = React.useMemo(
        () => getTimesheetPermissions(currentUser),
        [currentUser]
    );

    const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(now.getFullYear());
    const [userStatusMap, setUserStatusMap] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [currentDays, setCurrentDays] = useState<Date[]>([]);
    const [holidays, setHolidays] = useState<Record<string, any>>({});

    const [selectedDay, setSelectedDay] = useState<number>(0); // 0 = All

    // Generate year options
    const yearOptions = [];
    const currentYear = now.getFullYear();
    for (let year = 2025; year <= currentYear; year++) {
        yearOptions.push(year);
    }

    // Generate day options based on month/year
    const dayOptions = [{ value: "0", label: "All Days" }];
    if (selectedMonth !== 0) {
        const daysInMonth = getDaysInMonth(new Date(selectedYear, selectedMonth - 1));
        for (let i = 1; i <= daysInMonth; i++) {
            dayOptions.push({ value: i.toString(), label: i.toString() });
        }
    }

    // Effect to reset selectedDay if invalid for new month
    useEffect(() => {
        if (selectedMonth !== 0) {
            const daysInM = getDaysInMonth(new Date(selectedYear, selectedMonth - 1));
            if (selectedDay > daysInM) setSelectedDay(0);
        } else {
            if (selectedDay !== 0) setSelectedDay(0);
        }
    }, [selectedMonth, selectedYear]);

    // Fetch holidays
    useEffect(() => {
        const fetchHolidays = async () => {
            // ... existing holiday fetch logic is fine to keep, 
            // but strictly we should ensure this effect runs only when orgId changes.
            // The previous code had it right. I will re-implement carefully to not lose it if I'm replacing.
            // Actually, I am replacing the whole function body essentially? 
            // No, I am replacing from state declarations down to the end of component.
            // I'll re-include the fetchHolidays logic here to be safe.
            try {
                const holidaysCol = collection(db, "organization", organizationId, "holidays");
                const holidaysSnap = await getDocs(holidaysCol);
                const holidaysData: Record<string, any> = {};
                holidaysSnap.forEach((doc) => {
                    const data = doc.data();
                    holidaysData[doc.id] = { id: doc.id, date: data.date, reason: data.reason };
                });
                setHolidays(holidaysData);
            } catch (error) {
                console.error("❌ Error fetching holidays:", error);
                setHolidays({});
            }
        };
        fetchHolidays();
    }, [organizationId]);

    const fetchData = async () => {
        setLoading(true);

        let start: Date;
        let end: Date;

        if (selectedMonth === 0) {
            start = startOfYear(new Date(selectedYear, 0));
            end = selectedYear === now.getFullYear() ? now : endOfYear(start);
        } else {
            const mIdx = selectedMonth - 1;
            start = startOfMonth(new Date(selectedYear, mIdx));
            end = mIdx === now.getMonth() && selectedYear === now.getFullYear() ? now : endOfMonth(start);
        }

        // Generate array of days for columns
        let days = eachDayOfInterval({ start, end });

        // Filter by day if selected
        if (selectedMonth !== 0 && selectedDay > 0) {
            days = days.filter(d => getDate(d) === selectedDay);
        }

        setCurrentDays(days);

        const statusMap: Record<string, Record<string, string>> = {};

        const userPromises = users.map(async (u) => {
            try {
                const submittedDates = new Set<string>();
                const submittedWeeks = new Set<string>();

                // 1. Weekly architecture: TimeSheetsData/{userId}/weeks/{weekMonday}
                const tsColRef = collection(db, "organization", organizationId, "TimeSheetsData", u.id, "weeks");
                const userDocSnap = await getDocs(tsColRef);
                userDocSnap.docs.forEach(wkDoc => {
                    const wkData = wkDoc.data();
                    const entries = wkData.entries || [];
                    entries.forEach((e: any) => {
                        if (e?.date) {
                            submittedDates.add(e.date);
                            const dateObj = parseISO(e.date);
                            const weekStart = startOfWeek(dateObj, { weekStartsOn: 1 });
                            submittedWeeks.add(format(weekStart, "yyyy-MM-dd"));
                        }
                    });
                });

                // 2. Legacy path
                const oldRef = doc(db, "organization", organizationId, "timesheets", u.id);
                const oldSnap = await getDoc(oldRef);
                if (oldSnap.exists()) {
                    const legacyEntries = oldSnap.data().entries || [];
                    legacyEntries.forEach((e: any) => {
                        if (e?.date) {
                            submittedDates.add(e.date);
                            const dateObj = parseISO(e.date);
                            const weekStart = startOfWeek(dateObj, { weekStartsOn: 1 });
                            submittedWeeks.add(format(weekStart, "yyyy-MM-dd"));
                        }
                    });
                }

                const userStatus: Record<string, string> = {};

                days.forEach(day => {
                    const dateKey = format(day, "yyyy-MM-dd");
                    const isSun = isSunday(day);
                    const isHol = !!holidays[dateKey];

                    // Determine if user joined yet
                    let hasJoined = true;
                    if (u.joiningDate) {
                        try {
                            const joinDate = parseISO(u.joiningDate);
                            if (isBefore(day, joinDate)) hasJoined = false;
                        } catch { }
                    }

                    if (!hasJoined) {
                        userStatus[dateKey] = "NA";
                        return;
                    }

                    const weekStartStr = format(startOfWeek(day, { weekStartsOn: 1 }), "yyyy-MM-dd");

                    if (submittedDates.has(dateKey)) {
                        userStatus[dateKey] = "Submitted";
                    } else if (isSun || isHol) {
                        userStatus[dateKey] = "Off";
                    } else if (submittedWeeks.has(weekStartStr)) {
                        userStatus[dateKey] = "Submitted";
                    } else {
                        userStatus[dateKey] = "Missed";
                    }
                });
                statusMap[u.id] = userStatus;

            } catch (error) {
                console.error(`Error fetching for ${u.id}`, error);
            }
        });

        await Promise.all(userPromises);
        setUserStatusMap(statusMap);
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, [selectedMonth, selectedYear, organizationId, users, holidays, selectedDay]);

    // Helper to get initials
    const getInitials = (firstName: string, lastName: string) => {
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    };

    // Calculate Summary Stats
    const getUserStats = (userId: string) => {
        const statuses = Object.values(userStatusMap[userId] || {});
        return {
            submitted: statuses.filter(s => s === "Submitted").length,
            missed: statuses.filter(s => s === "Missed").length,
            off: statuses.filter(s => s === "Off").length,
        };
    };

    // Export to Excel
    const handleExport = async () => {
        // Dynamically import xlsx and file-saver
        const XLSX = await import("xlsx-js-style");
        const { saveAs } = await import("file-saver");

        const data: any[] = [];

        filteredUsers.forEach(user => {
            const row: any = {
                "Employee Name": `${user.firstName} ${user.lastName}`,
                "Email": user.email,
            };

            if (selectedDay > 0) {
                // Export only selected day
                // Note: currentDays index is selectedDay - 1
                if (currentDays[selectedDay - 1]) {
                    const day = currentDays[selectedDay - 1];
                    const key = format(day, "yyyy-MM-dd");
                    const status = userStatusMap[user.id]?.[key];
                    if (status === "Submitted") {
                        row[`Status (${key})`] = "✔";
                    } else if (status === "Missed") {
                        row[`Status (${key})`] = "✖";
                    } else {
                        row[`Status (${key})`] = status || "-";
                    }
                }
            } else {
                // Add daily status
                currentDays.forEach(day => {
                    const key = format(day, "yyyy-MM-dd");
                    const status = userStatusMap[user.id]?.[key];
                    if (status === "Submitted") {
                        row[key] = "✔";
                    } else if (status === "Missed") {
                        row[key] = "✖";
                    } else {
                        row[key] = status || "-";
                    }
                });
                // Add Summary
                const stats = getUserStats(user.id);
                row["Total Submitted"] = stats.submitted;
                row["Total Missed"] = stats.missed;
            }
            data.push(row);
        });

        const worksheet = XLSX.utils.json_to_sheet(data);

        // Apply styles
        const range = XLSX.utils.decode_range(worksheet['!ref'] as string);
        for (let R = range.s.r; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cell_address = { c: C, r: R };
                const cell_ref = XLSX.utils.encode_cell(cell_address);
                if (!worksheet[cell_ref]) continue;

                const cell = worksheet[cell_ref];

                // Style Header
                if (R === 0) {
                    cell.s = {
                        font: { bold: true, color: { rgb: "FFFFFF" } },
                        fill: { fgColor: { rgb: "4F46E5" } }, // Indigo background
                        alignment: { horizontal: "center" }
                    };
                } else {
                    // Style Data Cells
                    if (cell.v === "✔") {
                        cell.s = {
                            font: { color: { rgb: "00aa00" }, bold: true }, // Green
                            alignment: { horizontal: "center" }
                        };
                    } else if (cell.v === "✖") {
                        cell.s = {
                            font: { color: { rgb: "ff0000" }, bold: true }, // Red
                            alignment: { horizontal: "center" }
                        };
                    } else {
                        cell.s = { alignment: { horizontal: "center" } };
                    }
                }
            }
        }

        // Auto-width columns
        const colWidths = Object.keys(data[0] || {}).map(key => ({ wch: Math.max(key.length, 15) }));
        worksheet["!cols"] = colWidths;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "TimeSheet Data");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "buffer" });
        const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });

        const monthLabel = selectedMonth === 0 ? "AllMonths" : selectedMonth;
        saveAs(blob, `TimeSheet_Report_${selectedYear}_${monthLabel}${selectedDay > 0 ? `_Day${selectedDay}` : ''}.xlsx`);
    };


    return (
        <div className={`flex flex-col w-full h-full transition-colors duration-200 ${isDark ? 'bg-[#0B0B0F] text-white' : 'bg-transparent text-slate-900'}`}>
            <style>
                {`
                    .gold-scrollbar {
                        scrollbar-width: thin;
                        scrollbar-color: ${isDark ? '#B59143' : '#FBD282'} transparent;
                    }
                    .gold-scrollbar::-webkit-scrollbar {
                        width: 6px;
                        height: 6px;
                    }
                    .gold-scrollbar::-webkit-scrollbar-track {
                        background: transparent;
                    }
                    .gold-scrollbar::-webkit-scrollbar-thumb {
                        background: linear-gradient(to bottom, #FBD282, #B59143);
                        border-radius: 99px;
                    }
                    .gold-scrollbar::-webkit-scrollbar-thumb:hover {
                         background: linear-gradient(to bottom, #E8C16C, #A7843D);
                    }
                `}
            </style>

            {/* Header Section */}
            <div className={`flex justify-between items-center p-4 transition-all duration-300 ${theme.headerGradient} rounded-md mb-6 border ${theme.border}`}>
                {isDark && (
                    <>
                        <span className={theme.headerOverlayGlow} />
                        <span className={theme.headerOverlayTopLine} />
                        <span className={theme.headerOverlayBottomGlow} />
                        <span className={theme.headerOverlaySweep} />
                    </>
                )}
                <div className="flex flex-col gap-1 relative z-10">
                    <h1 className={`text-2xl font-bold ${theme.text}`}>Overview</h1>
                    <p className={`text-xs ${theme.textMuted}`}>
                        Monitor all daily check-ins and timesheets for employees.
                    </p>
                </div>
                {/* Tab Switch */}
                <div className={`relative z-10 flex items-center p-1 rounded-lg border ${isDark ? 'bg-[#1A1C1F] border-[#2E2F33]' : 'bg-white border-orange-200'}`}>
                    <button
                        onClick={() => onTabChange?.('CheckIn')}
                        className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${activeTab === 'CheckIn'
                            ? (isDark ? 'bg-amber-500/20 text-amber-500' : 'bg-orange-100 text-orange-700')
                            : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-slate-500 hover:text-slate-700')
                            }`}
                    >
                        CheckIns
                    </button>
                    <button
                        onClick={() => onTabChange?.('TimeSheet')}
                        className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${activeTab === 'TimeSheet' || !activeTab
                            ? (isDark ? 'bg-amber-500/20 text-amber-500' : 'bg-orange-100 text-orange-700')
                            : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-slate-500 hover:text-slate-700')
                            }`}
                    >
                        TimeSheets
                    </button>
                </div>
            </div>

            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    {showBackButton && onBack && (
                        <button
                            onClick={onBack}
                            className={`flex items-center gap-2 px-3 py-1.5 border rounded-lg transition-all duration-200 font-medium ${isDark ? 'border-[#B59143] text-[#FBD282] hover:bg-[#2A3441]' : 'border-[#E8C16C] text-[#B59143] hover:bg-[#FFF7E6]'}`}
                            aria-label="Go back"
                        >
                            <ArrowLeft size={16} />
                            Back
                        </button>
                    )}
                    <CustomDropdown
                        value={selectedUserId}
                        onChange={(value) => setSelectedUserId(value)}
                        isDark={isDark}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg w-64"
                        options={[{ value: "all", label: "All Users" }, ...sortedUsers.map(u => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }))]}
                    />
                </div>

                <div className="flex items-center gap-3">
                    <CustomDropdown
                        value={selectedDay.toString()}
                        onChange={(value) => setSelectedDay(+value)}
                        isDark={isDark}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg w-32"
                        options={dayOptions}
                    />
                    <CustomDropdown
                        value={selectedMonth.toString()}
                        onChange={(value) => setSelectedMonth(+value)}
                        isDark={isDark}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg w-40"
                        options={[
                            { value: "0", label: "All Months" },
                            ...MONTHS.map((m, i) => ({ value: (i + 1).toString(), label: m })).filter(opt => {
                                if (selectedYear === now.getFullYear()) return (parseInt(opt.value) - 1) <= now.getMonth();
                                return true;
                            })
                        ]}
                    />
                    <CustomDropdown
                        value={selectedYear.toString()}
                        onChange={(value) => setSelectedYear(+value)}
                        isDark={isDark}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg w-32"
                        options={yearOptions.map(y => ({ value: y.toString(), label: y.toString() }))}
                    />
                    {tsPerms.canExport && <button
                        onClick={handleExport}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors shadow-sm ${isDark ? 'bg-gradient-to-r from-[#FBD282] to-[#B59143] text-white hover:from-[#E8C16C] hover:to-[#A7843D]' : 'bg-gradient-to-r from-[#FBD282] to-[#B59143] text-white hover:from-[#E8C16C] hover:to-[#A7843D]'}`}
                    >
                        <Download size={16} />
                        Export
                    </button>}
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-hidden flex flex-col">
                {loading ? (
                    <div className="flex-1 flex flex-col items-center justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mb-4"></div>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Loading timesheet data...</p>
                    </div>
                ) : (
                    <div className={`flex flex-col rounded-xl border overflow-hidden max-h-full ${isDark ? 'border-[#2E2F33] bg-[#1A1C1F]' : 'border-slate-200 bg-white'}`}>
                        <div className="flex-1 overflow-auto relative gold-scrollbar">
                            <table className="border-collapse w-full min-w-max text-sm text-left">
                                <thead className={`sticky top-0 z-20 ${isDark ? 'bg-[#1A1C1F] text-gray-400' : 'bg-white text-slate-500'}`}>
                                    <tr>
                                        <th className={`sticky left-0 z-20 px-6 py-2 font-semibold text-xs uppercase tracking-wider border-b border-r min-w-[200px] ${isDark ? 'bg-[#1A1C1F] border-[#2E2F33]' : 'bg-white border-slate-200'}`}>
                                            Employee
                                        </th>
                                        {/* Date Columns */}
                                        {currentDays.map(day => {
                                            const isWeekend = isSunday(day);
                                            return (
                                                <th key={day.toISOString()} className={`px-2 py-2 text-center border-b border-r min-w-[50px] last:border-r-0 ${isDark ? 'border-[#2E2F33] bg-[#1A1C1F]' : 'border-slate-200 bg-white'} ${isWeekend ? (isDark ? 'bg-[#232529]' : 'bg-slate-50') : ''}`}>
                                                    <div className="flex flex-col items-center">
                                                        {selectedMonth === 0 && (
                                                            <span className={`text-[9px] font-bold uppercase ${isDark ? 'text-amber-500' : 'text-amber-600'}`}>{format(day, 'MMM')}</span>
                                                        )}
                                                        <span className={`text-[10px] font-bold uppercase mb-0.5 ${isWeekend ? 'text-rose-500' : ''}`}>{format(day, 'EEE')}</span>
                                                        <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{getDate(day)}</span>
                                                    </div>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                    {/* Summary Row */}
                                    <tr className={`font-semibold border-b ${isDark ? 'bg-[#232529] border-[#2E2F33]' : 'bg-slate-50 border-slate-200'}`}>
                                        <td className={`sticky left-0 z-20 px-6 py-2 border-r text-xs ${isDark ? 'bg-[#232529] border-[#2E2F33] text-white' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>
                                            Total (S | M)
                                        </td>
                                        {currentDays.map(day => {
                                            const dateKey = format(day, "yyyy-MM-dd");
                                            let submittedCount = 0;
                                            let missedCount = 0;
                                            filteredUsers.forEach(u => {
                                                const status = userStatusMap[u.id]?.[dateKey];
                                                if (status === "Submitted") submittedCount++;
                                                if (status === "Missed") missedCount++;
                                            });

                                            return (
                                                <td key={day.toISOString()} className={`px-1 py-1 text-center border-r last:border-r-0 text-[10px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}>
                                                    <div className="flex items-center justify-center gap-1">
                                                        <span className="text-emerald-600 font-bold">{submittedCount}</span>
                                                        <span className="text-slate-300">|</span>
                                                        <span className="text-rose-600 font-bold">{missedCount}</span>
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-slate-100'}`}>

                                    {filteredUsers.map(user => {
                                        const stats = getUserStats(user.id);
                                        return (
                                            <tr key={user.id} className={`group transition-colors ${isDark ? 'hover:bg-[#232529]' : 'hover:bg-slate-50'}`}>
                                                {/* User Name Column */}
                                                <td className={`sticky left-0 z-10 px-6 py-2 border-r ${isDark ? 'bg-[#1A1C1F] group-hover:bg-[#232529] border-[#2E2F33]' : 'bg-white group-hover:bg-slate-50 border-slate-200'}`}>
                                                    <div className="flex items-center gap-3">
                                                        {/* Initials removed */}
                                                        <div>
                                                            <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{user.firstName} {user.lastName}</p>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Daily Status Columns */}
                                                {currentDays.map(day => {
                                                    const key = format(day, "yyyy-MM-dd");
                                                    const status = userStatusMap[user.id]?.[key];
                                                    const isWeekend = isSunday(day);
                                                    const isHol = !!holidays[key];

                                                    let cellContent = null;
                                                    let cellClass = "";

                                                    if (status === "Submitted") {
                                                        cellContent = <Check size={16} strokeWidth={3} className="text-emerald-600 dark:text-emerald-500 mx-auto" />;
                                                    } else if (status === "Missed") {
                                                        cellContent = <X size={16} strokeWidth={3} className="text-rose-600 dark:text-rose-500 mx-auto" />;
                                                    } else if (status === "Off") {
                                                        cellClass = isDark ? "bg-[#23252b] opacity-50" : "bg-slate-50";
                                                        cellContent = <span className={`text-xs font-mono font-medium ${isDark ? 'text-gray-600' : 'text-slate-300'}`}>-</span>;
                                                        if (isHol) {
                                                            cellContent = <span title={holidays[key]?.reason} className="text-[10px] px-1 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-500 font-bold">H</span>;
                                                        }
                                                    }

                                                    return (
                                                        <td key={key} className={`px-1 py-1 text-center border-r last:border-r-0 ${isDark ? 'border-[#2E2F33]' : 'border-slate-100'} ${cellClass} ${isWeekend ? (isDark ? 'bg-[#181a1d]' : 'bg-slate-50/50') : ''}`}>
                                                            <div className="flex items-center justify-center w-full h-full">
                                                                {cellContent}
                                                            </div>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>



                    </div>
                )}
            </div>
        </div>
    );
};

export default TimeSheetTable;
